import { Head, Link, router } from '@inertiajs/react'
import { useState } from 'react'
import { ArrowLeft, Plus, Pencil, Trash2, Calendar, ToggleLeft, List, X } from 'lucide-react'
import NavigationRail from '@/components/NavigationRail'
import SettingsNav from '@/components/SettingsNav'
import type { Agent } from '@/types/shared'

interface DataAttribute {
  id: number
  name: string
  label: string | null
  description: string | null
  dataType: string
  model: string
  apiWritable: boolean
  custom: boolean
  listOptions: string[]
  createdAt: string
}

interface DataAttributesProps {
  dataAttributes: DataAttribute[]
  currentAgent: Agent
}

const DATA_TYPES = [
  { value: 'string', label: 'Texto', icon: 'Aa' },
  { value: 'integer', label: 'Número', icon: '1²³' },
  { value: 'float', label: 'Decimal', icon: '0.2' },
  { value: 'boolean', label: 'Verdadeiro/Falso', icon: null, iconComponent: ToggleLeft },
  { value: 'date', label: 'Data', icon: null, iconComponent: Calendar },
  { value: 'datetime', label: 'Data e Hora', icon: null, iconComponent: Calendar },
  { value: 'list', label: 'Lista', icon: null, iconComponent: List },
]

function getTypeInfo(dataType: string) {
  return DATA_TYPES.find(t => t.value === dataType) || DATA_TYPES[0]
}

function TypeIcon({ dataType, className = '' }: { dataType: string; className?: string }) {
  const info = getTypeInfo(dataType)
  if (info.icon) {
    return <span className={`text-xs font-mono font-bold text-gray-500 ${className}`}>{info.icon}</span>
  }
  if (info.iconComponent) {
    const Icon = info.iconComponent
    return <Icon className={`w-4 h-4 text-gray-500 ${className}`} />
  }
  return null
}

interface ModalFormData {
  name: string
  label: string
  description: string
  dataType: string
  model: string
  listOptions: string[]
}

const EMPTY_FORM: ModalFormData = {
  name: '',
  label: '',
  description: '',
  dataType: 'string',
  model: 'contact',
  listOptions: [],
}

export default function DataAttributes({ dataAttributes, currentAgent }: DataAttributesProps) {
  const [activeTab, setActiveTab] = useState<'contact' | 'company'>('contact')
  const [showModal, setShowModal] = useState(false)
  const [editingAttribute, setEditingAttribute] = useState<DataAttribute | null>(null)
  const [form, setForm] = useState<ModalFormData>(EMPTY_FORM)
  const [newOption, setNewOption] = useState('')
  const [processing, setProcessing] = useState(false)

  const filteredAttributes = dataAttributes.filter(da => da.model === activeTab)

  const openCreate = () => {
    setEditingAttribute(null)
    setForm({ ...EMPTY_FORM, model: activeTab })
    setShowModal(true)
  }

  const openEdit = (attr: DataAttribute) => {
    setEditingAttribute(attr)
    setForm({
      name: attr.name,
      label: attr.label || '',
      description: attr.description || '',
      dataType: attr.dataType,
      model: attr.model,
      listOptions: attr.listOptions || [],
    })
    setShowModal(true)
  }

  const handleAddOption = () => {
    const trimmed = newOption.trim()
    if (trimmed && !form.listOptions.includes(trimmed)) {
      setForm({ ...form, listOptions: [...form.listOptions, trimmed] })
      setNewOption('')
    }
  }

  const handleRemoveOption = (index: number) => {
    setForm({ ...form, listOptions: form.listOptions.filter((_, i) => i !== index) })
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setProcessing(true)

    const payload = {
      data_attribute: {
        name: form.name,
        label: form.label || null,
        description: form.description || null,
        data_type: form.dataType,
        model: form.model,
        list_options: form.dataType === 'list' ? form.listOptions : [],
      }
    }

    if (editingAttribute) {
      router.patch(`/settings/data-attributes/${editingAttribute.id}`, payload, {
        preserveScroll: true,
        onFinish: () => {
          setProcessing(false)
          setShowModal(false)
        },
      })
    } else {
      router.post('/settings/data-attributes', payload, {
        preserveScroll: true,
        onFinish: () => {
          setProcessing(false)
          setShowModal(false)
        },
      })
    }
  }

  const handleDelete = (attr: DataAttribute) => {
    if (confirm(`Tem certeza que deseja remover o atributo "${attr.label || attr.name}"?`)) {
      router.delete(`/settings/data-attributes/${attr.id}`, { preserveScroll: true })
    }
  }

  return (
    <>
      <Head title="Atributos de Dados" />
      <div className="h-screen flex bg-gray-50">
        <NavigationRail currentAgent={currentAgent} activePage="settings" />

        <div className="flex-1 flex flex-col min-w-0">
          <header className="h-14 bg-white border-b border-gray-200 flex items-center px-6">
            <Link
              href="/inbox"
              className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 text-sm mr-4"
            >
              <ArrowLeft className="w-4 h-4" />
            </Link>
            <h1 className="text-lg font-semibold text-gray-900">Configurações</h1>
          </header>

          <div className="flex-1 flex min-h-0">
            <SettingsNav activePage="data-attributes" isAdmin={currentAgent.role === 'admin'} />

            <main className="flex-1 overflow-auto p-6">
            <div className="max-w-4xl">
              {/* Tabs and Create button */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setActiveTab('contact')}
                    className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors cursor-pointer ${
                      activeTab === 'contact'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Contatos
                  </button>
                  <button
                    onClick={() => setActiveTab('company')}
                    className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors cursor-pointer ${
                      activeTab === 'company'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Empresas
                  </button>
                </div>
                <button
                  onClick={openCreate}
                  className="inline-flex items-center gap-2 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors cursor-pointer"
                >
                  <Plus className="w-4 h-4" />
                  Criar atributo
                </button>
              </div>

              {/* Attributes list */}
              <div className="bg-white rounded-lg border border-gray-200">
                {filteredAttributes.length === 0 ? (
                  <div className="p-8 text-center text-gray-500 text-sm">
                    Nenhum atributo definido para {activeTab === 'contact' ? 'contatos' : 'empresas'}.
                  </div>
                ) : (
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-100">
                        <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Nome</th>
                        <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Tipo</th>
                        <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Descrição</th>
                        <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Origem</th>
                        <th className="w-20 px-4 py-3"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {filteredAttributes.map((attr) => {
                        const typeInfo = getTypeInfo(attr.dataType)
                        return (
                          <tr key={attr.id} className="hover:bg-gray-50 group">
                            <td className="px-4 py-3">
                              <div className="font-medium text-gray-900 text-sm">{attr.label || attr.name}</div>
                              {attr.label && (
                                <div className="text-xs text-gray-400 font-mono">{attr.name}</div>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <TypeIcon dataType={attr.dataType} />
                                <span>{typeInfo.label}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500 max-w-[200px] truncate">
                              {attr.description || '—'}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                attr.custom
                                  ? 'bg-blue-50 text-blue-700'
                                  : 'bg-gray-100 text-gray-600'
                              }`}>
                                {attr.custom ? 'Personalizado' : 'Importado'}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                  onClick={() => openEdit(attr)}
                                  className="p-1.5 text-gray-400 hover:text-gray-600 rounded cursor-pointer"
                                  title="Editar"
                                >
                                  <Pencil className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => handleDelete(attr)}
                                  className="p-1.5 text-gray-400 hover:text-red-600 rounded cursor-pointer"
                                  title="Remover"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </main>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/40" onClick={() => setShowModal(false)} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingAttribute ? 'Editar Atributo' : 'Criar Atributo'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome (chave)</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="ex: job_title"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rótulo</label>
                <input
                  type="text"
                  value={form.label}
                  onChange={(e) => setForm({ ...form, label: e.target.value })}
                  placeholder="ex: Cargo"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
                <input
                  type="text"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Descrição opcional"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                  <select
                    value={form.dataType}
                    onChange={(e) => setForm({ ...form, dataType: e.target.value, listOptions: [] })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white"
                  >
                    {DATA_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Modelo</label>
                  <select
                    value={form.model}
                    onChange={(e) => setForm({ ...form, model: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white"
                  >
                    <option value="contact">Contato</option>
                    <option value="company">Empresa</option>
                  </select>
                </div>
              </div>

              {form.dataType === 'list' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Opções da lista</label>
                  <div className="space-y-2">
                    {form.listOptions.map((opt, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className="flex-1 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded text-sm">{opt}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveOption(i)}
                          className="text-gray-400 hover:text-red-500 cursor-pointer"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newOption}
                        onChange={(e) => setNewOption(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddOption() } }}
                        placeholder="Nova opção..."
                        className="flex-1 px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                      />
                      <button
                        type="button"
                        onClick={handleAddOption}
                        className="px-3 py-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium cursor-pointer"
                      >
                        Adicionar
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={processing || !form.name.trim() || (form.dataType === 'list' && form.listOptions.length === 0)}
                  className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
                >
                  {processing ? 'Salvando...' : editingAttribute ? 'Salvar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  )
}
